import { Drawer, DrawerContent, DrawerOverlay, Modal, ModalContent, ModalOverlay } from '@chakra-ui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIsMobile } from '../../helpers/useIsMobile';
import { logger } from '../../helpers/logger';
import { generateUuid } from '../../helpers/uuid';
import { Box } from '../UI-CHAKRA/Box';
import {
  FreeSampleCatalogBrand,
  FreeSampleContext,
  FreeSampleDeliveryAddress,
  FreeSampleErrorContent,
  FreeSampleEstablishmentOption,
  FreeSampleMedicine,
  FreeSamplePrescriberForm,
  FreeSamplePrescriberInfo,
  FreeSampleProgram,
  FreeSampleRequestOutput,
  applyPrescriberForm,
  getKnownRequestError,
  groupCatalogByBrand,
  toPrescriberForm,
} from '../../interfaces/freeSample';
import { FreeSampleService } from './freeSampleService';
import FreeSampleLaboratoriesStep from './FreeSampleLaboratoriesStep';
import FreeSampleProductsStep from './FreeSampleProductsStep';
import FreeSampleAddressStep from './FreeSampleAddressStep';
import FreeSamplePrescriberStep from './FreeSamplePrescriberStep';
import FreeSampleConsentStep from './FreeSampleConsentStep';
import FreeSampleSuccessStep from './FreeSampleSuccessStep';
import FreeSampleInterestStep from './FreeSampleInterestStep';
import FreeSampleInterestSuccessStep from './FreeSampleInterestSuccessStep';
import FreeSampleEmptyStep from './FreeSampleEmptyStep';
import FreeSampleErrorStep from './FreeSampleErrorStep';
import { TrackerEventService } from '../../containers/prescription/trackerEventService';
import { usePrescriptionStore } from '../../stores/prescriptionStore';
import { amplitudeModule, SampleRequestCompletedProduct, SampleRequestType } from '../../shared/amplitude';
import { NO_SPECIALTY } from '../../helpers/rqeSpecialties';
import { APP_VIEWPORT_HEIGHT } from '../../helpers/embeddedViewport';

type Step =
  | 'catalog'
  | 'brandDetail'
  | 'address'
  | 'prescriber'
  | 'consent'
  | 'success'
  | 'interest'
  | 'interestSuccess'
  | 'empty'
  | 'error';

const EMPTY_ADDRESS: FreeSampleDeliveryAddress = {
  postalCode: '',
  address: '',
  number: '',
  withoutNumber: false,
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
};

interface FreeSampleSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  establishmentOptions: FreeSampleEstablishmentOption[];
  prescriber: FreeSamplePrescriberInfo;
  initialContext?: FreeSampleContext;
}

const FreeSampleSidebar: React.FC<FreeSampleSidebarProps> = ({
  isOpen,
  onClose,
  establishmentOptions,
  prescriber,
  initialContext,
}) => {
  const isMobile = useIsMobile();
  const [step, setStep] = useState<Step>('catalog');
  const prescription = usePrescriptionStore((store) => store.prescription);
  const prescriberConfiguration = usePrescriptionStore((store) => store.prescriberConfiguration);
  const flow = initialContext ? 'prescricao' : 'catalogo';
  const entryPoint = initialContext ? 'prescription' : 'menu';

  const [program, setProgram] = useState<FreeSampleProgram | null>(null);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalog, setCatalog] = useState<FreeSampleMedicine[]>([]);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [enteredFromContext, setEnteredFromContext] = useState(false);

  const [selectedBrand, setSelectedBrand] = useState<FreeSampleCatalogBrand | null>(null);
  const [selectedSkus, setSelectedSkus] = useState<string[]>([]);
  const [selectedInterestItem, setSelectedInterestItem] = useState<FreeSampleMedicine | null>(null);
  const [prescriberForm, setPrescriberForm] = useState<FreeSamplePrescriberForm>(() => toPrescriberForm(prescriber));

  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState<number | null>(null);
  const [deliveryAddress, setDeliveryAddress] = useState<FreeSampleDeliveryAddress>(EMPTY_ADDRESS);
  const [confirmsAttendanceLocation, setConfirmsAttendanceLocation] = useState(false);

  const [shippingConsent, setShippingConsent] = useState(false);
  const [communicationsConsent, setCommunicationsConsent] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorContent, setErrorContent] = useState<FreeSampleErrorContent | null>(null);

  const previousIsOpenRef = useRef<boolean>(isOpen);
  // Especialidade é obrigatória no envio e vai pré-preenchida no formulário: o nome do idRqe do
  // cadastro; se faltar, do fallback de RQE (buscado em paralelo ao abrir); se ainda assim faltar,
  // "SEM ESPECIALIDADE". O médico pode trocar por outra opção da lista.
  const [isResolvingSpecialty, setIsResolvingSpecialty] = useState(false);
  const specialtyRequestIdRef = useRef(0);
  // Incrementado a cada abertura: respostas de um ciclo anterior do sidebar são descartadas.
  const catalogRequestIdRef = useRef(0);
  // Uma chave por tentativa de envio (gerada ao entrar no consentimento/interesse), reaproveitada
  // em retentativas da mesma tela — gerar a cada clique anularia a idempotência do backend.
  const idempotencyKeyRef = useRef<string>(generateUuid());

  const filteredBrands = useMemo(() => {
    const normalized = catalogSearch.trim().toLowerCase();
    const filtered = !normalized
      ? catalog
      : catalog.filter(
          (medicine) =>
            medicine.brand.toLowerCase().includes(normalized) ||
            medicine.medicineName.toLowerCase().includes(normalized)
        );
    return groupCatalogByBrand(filtered);
  }, [catalog, catalogSearch]);

  const fetchCatalog = async () => {
    const requestId = ++catalogRequestIdRef.current;
    const isStale = () => requestId !== catalogRequestIdRef.current;

    setIsLoadingCatalog(true);
    try {
      const activeProgram = await FreeSampleService.getActiveProgram();
      if (isStale()) return;
      setProgram(activeProgram);

      if (!activeProgram) {
        setCatalog([]);
        setStep('empty');
        return;
      }

      const medicines = await FreeSampleService.getCatalog(activeProgram.id, {
        crmNumber: prescriber.crmNumber,
        crmState: prescriber.crmState,
        cpf: prescriber.cpf,
      });
      if (isStale()) return;
      setCatalog(medicines);

      if (medicines.length === 0) {
        setStep('empty');
        return;
      }

      if (initialContext) {
        const contextBrand = initialContext.brand.toLowerCase();
        const brandGroup = groupCatalogByBrand(medicines).find((group) => group.brand.toLowerCase() === contextBrand);
        if (brandGroup) {
          const contextSkus = initialContext.skus ?? [];
          setEnteredFromContext(true);
          setSelectedBrand(brandGroup);
          setSelectedSkus(
            brandGroup.items.filter((item) => item.eligible && contextSkus.includes(item.sku)).map((item) => item.sku)
          );
          setStep('brandDetail');
          return;
        }
      }

      setStep('catalog');
    } catch {
      if (isStale()) return;
      setStep('error');
    } finally {
      if (!isStale()) setIsLoadingCatalog(false);
    }
  };

  const resetState = () => {
    setStep('catalog');
    setProgram(null);
    setCatalogSearch('');
    setCatalog([]);
    setEnteredFromContext(false);
    setSelectedBrand(null);
    setSelectedSkus([]);
    setSelectedInterestItem(null);
    setPrescriberForm(toPrescriberForm(prescriber));

    const defaultOption = establishmentOptions.length === 1 ? establishmentOptions[0] : null;
    setSelectedEstablishmentId(defaultOption?.id ?? null);
    setDeliveryAddress(defaultOption?.address ?? EMPTY_ADDRESS);
    setConfirmsAttendanceLocation(false);

    setShippingConsent(false);
    setCommunicationsConsent(false);
    setIsSubmitting(false);
    setErrorContent(null);
  };

  // Nenhum item aceito: mostra a mensagem específica se algum item falhou por um motivo conhecido,
  // senão a genérica. A `message` crua vai só pro log.
  const handleRequestResult = (result: FreeSampleRequestOutput, successStep: Step) => {
    if (result.results.some((item) => item.isSuccess)) {
      setStep(successStep);
      return;
    }

    logger.warn('[FreeSample] Solicitação recusada:', { results: result.results });
    emitRequestException(
      successStep,
      result.results.map((item) => item.message).filter(Boolean).join('; ') || 'Solicitação recusada'
    );
    setErrorContent(getKnownRequestError(result.results) ?? null);
    setStep('error');
  };

  useEffect(() => {
    const wasOpen = previousIsOpenRef.current;
    previousIsOpenRef.current = isOpen;

    if (!wasOpen && isOpen) {
      resetState();
      fetchCatalog();
      resolveSpecialty();
      amplitudeModule.sampleRequestStarted({
        entry_point: entryPoint,
        ...(entryPoint === 'prescription' && {
          preselected_product_id: JSON.stringify(initialContext?.skus ?? []),
          validation_code: prescription?.CodigoValidacao ?? '',
        }),
        ...amplitudePartner,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const resolveSpecialty = async () => {
    const requestId = ++specialtyRequestIdRef.current;

    if (prescriber.specialty) {
      setIsResolvingSpecialty(false);
      return;
    }

    setIsResolvingSpecialty(true);
    const specialty = prescriber.prescriberId
      ? await FreeSampleService.getSpecialtyFromRqes(prescriber.prescriberId)
      : '';
    if (requestId !== specialtyRequestIdRef.current) return;

    setPrescriberForm((prev) => ({ ...prev, specialty: specialty || NO_SPECIALTY }));
    setIsResolvingSpecialty(false);
  };

  const handleSelectBrand = (brand: FreeSampleCatalogBrand) => {
    setSelectedBrand(brand);
    setSelectedSkus([]);
    setStep('brandDetail');
  };

  const handleToggleSku = (sku: string) => {
    setSelectedSkus((prev) => (prev.includes(sku) ? prev.filter((value) => value !== sku) : [...prev, sku]));
  };

  const handleSelectInterest = (item: FreeSampleMedicine) => {
    setSelectedInterestItem(item);
    setStep('address');
  };

  const handleContinueEligible = () => {
    setSelectedInterestItem(null);
    setStep('address');
  };

  const handleSelectEstablishment = (option: FreeSampleEstablishmentOption) => {
    setSelectedEstablishmentId(option.id);
    setDeliveryAddress(option.address);
  };

  // O endereço é comum aos dois fluxos: o de interesse segue direto pra tela de interesse (que já
  // coleta e-mail/telefone/nascimento), o elegível passa pela tela de dados do médico.
  const handleConfirmAddress = () => {
    if (selectedInterestItem) {
      setShippingConsent(false);
      setCommunicationsConsent(false);
      idempotencyKeyRef.current = generateUuid();
      setStep('interest');
      return;
    }

    setStep('prescriber');
  };

  const handleConfirmPrescriber = () => {
    setShippingConsent(false);
    setCommunicationsConsent(false);
    idempotencyKeyRef.current = generateUuid();
    setStep('consent');
  };

  const buildPrescriberPayload = () => applyPrescriberForm(prescriber, prescriberForm);

  const handleConfirmConsent = async () => {
    if (!selectedBrand || selectedSkus.length === 0 || !program) return;

    setIsSubmitting(true);
    try {
      const result = await FreeSampleService.submitRequest({
        freeSampleId: program.id,
        idempotencyKey: idempotencyKeyRef.current,
        items: selectedSkus.map((sku) => ({ brand: selectedBrand.brand, sku })),
        prescriber: buildPrescriberPayload(),
        deliveryAddress,
        shippingConsent,
        communicationsConsent,
      });

      emitFeedback(result.results.some((item) => item.isSuccess) ? 'success' : 'error');
      handleRequestResult(result, 'success');
    } catch (error) {
      emitFeedback('error');
      emitRequestException('success', error instanceof Error ? error.message : String(error));
      setErrorContent(null);
      setStep('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmInterest = async () => {
    if (!selectedBrand || !selectedInterestItem || !program) return;

    setIsSubmitting(true);
    try {
      const result = await FreeSampleService.submitRequest({
        freeSampleId: program.id,
        idempotencyKey: idempotencyKeyRef.current,
        items: [{ brand: selectedBrand.brand }],
        prescriber: buildPrescriberPayload(),
        deliveryAddress,
        shippingConsent,
        communicationsConsent,
      });

      emitFeedback(result.results.some((item) => item.isSuccess) ? 'success' : 'error');
      handleRequestResult(result, 'interestSuccess');
    } catch (error) {
      emitFeedback('error');
      emitRequestException('interestSuccess', error instanceof Error ? error.message : String(error));
      setErrorContent(null);
      setStep('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mesmo evento de feedback nos dois fluxos (solicitação elegível e interesse).
  const emitFeedback = (status: 'success' | 'error') => {
    handleEventEmission('backoffice_campaign_amostra_gratis_feedback', { statusCadastro: status });
  };

  const handleClose = () => {
    onClose();
  };

  // Amplitude (spec de produto 2026-10-06): só início, conclusão e erro de envio — as etapas
  // intermediárias não geram evento no Amplitude.
  const amplitudePartner = {
    partner_id: prescriberConfiguration?.idParceiro ?? 0,
    partner_name: prescriberConfiguration?.Nome ?? '',
    establishment_name: prescription?.Estabelecimento?.Nome ?? '',
  };

  const emitRequestException = (successStep: Step, errorMessage: string) => {
    amplitudeModule.exceptionEncountered({
      screen_name: successStep === 'interestSuccess' ? 'free_sample_interest' : 'free_sample_consent',
      error_message: errorMessage,
      ...amplitudePartner,
    });
  };

  const getAddressSource = (): 'saved' | 'edited' | 'new' => {
    const savedAddress = establishmentOptions.find((option) => option.id === selectedEstablishmentId)?.address;
    if (!savedAddress) return 'new';

    const fields = Object.keys(savedAddress) as Array<keyof FreeSampleDeliveryAddress>;
    return fields.every((field) => savedAddress[field] === deliveryAddress[field]) ? 'saved' : 'edited';
  };

  // Disparado pelas telas de confirmação ao serem exibidas (uma vez por solicitação).
  const emitRequestCompleted = () => {
    const brand = selectedBrand?.brand ?? '';
    const toProduct = (item: FreeSampleMedicine, requestType: 'sample' | 'interest'): SampleRequestCompletedProduct => ({
      id: item.sku,
      name: item.medicineName,
      laboratory_id: brand,
      laboratory_name: brand,
      request_type: requestType,
    });
    const products = selectedInterestItem
      ? [toProduct(selectedInterestItem, 'interest')]
      : (selectedBrand?.items ?? []).filter((item) => selectedSkus.includes(item.sku)).map((item) => toProduct(item, 'sample'));
    const requestTypes = new Set(products.map((product) => product.request_type));
    const requestType: SampleRequestType = requestTypes.size > 1 ? 'mixed' : products[0]?.request_type ?? 'sample';

    amplitudeModule.sampleRequestCompleted({
      sample_request_id: idempotencyKeyRef.current,
      request_type: requestType,
      products,
      products_count: products.length,
      address_source: getAddressSource(),
      entry_point: entryPoint,
      ...(entryPoint === 'prescription' && { validation_code: prescription?.CodigoValidacao ?? '' }),
      ...amplitudePartner,
    });
  };

  const handleEventEmission = (eventName: string, extraParams: Record<string, unknown> = {}) => {
    if (!prescription?.idPrescricao) return;

    TrackerEventService.track(prescription.idPrescricao, eventName, {
      flow,
      idPrescricao: prescription.idPrescricao,
      idParceiro: prescriberConfiguration?.idParceiro,
      idMedico: prescription?.Prescritor?.idPrescritor,
      ambiente: process.env.REACT_APP_ENVIRONMENT || 'staging',
      isMobile: isMobile,
      ...extraParams,
    }).catch((error) => logger.error('[FreeSample] Erro ao enviar evento:', { eventName, error }));
  };

  const selectedProductName = selectedInterestItem
    ? selectedInterestItem.medicineName
    : selectedSkus.length === 1
      ? (selectedBrand?.items.find((i) => i.sku === selectedSkus[0])?.medicineName ?? '')
      : `${selectedSkus.length} itens`;

  const renderContent = () => {
    switch (step) {
      case 'brandDetail':
        return selectedBrand ? (
          <FreeSampleProductsStep
            brand={selectedBrand}
            selectedSkus={selectedSkus}
            onToggleSku={handleToggleSku}
            onSelectInterest={handleSelectInterest}
            onContinue={handleContinueEligible}
            onBack={enteredFromContext ? handleClose : () => setStep('catalog')}
            onClose={handleClose}
            onEventEmission={handleEventEmission}
          />
        ) : null;
      case 'address':
        return (
          <FreeSampleAddressStep
            laboratoryName={selectedBrand?.brand ?? ''}
            productNames={
              selectedInterestItem
                ? [selectedInterestItem.medicineName]
                : (selectedBrand?.items ?? [])
                    .filter((item) => selectedSkus.includes(item.sku))
                    .map((item) => item.medicineName)
            }
            establishmentOptions={establishmentOptions}
            selectedEstablishmentId={selectedEstablishmentId}
            onSelectEstablishment={handleSelectEstablishment}
            address={deliveryAddress}
            onAddressChange={setDeliveryAddress}
            confirmsAttendanceLocation={confirmsAttendanceLocation}
            onConfirmsAttendanceLocationChange={setConfirmsAttendanceLocation}
            onBack={() => setStep('brandDetail')}
            onConfirm={handleConfirmAddress}
            onClose={handleClose}
            onEventEmission={handleEventEmission}
          />
        );
      case 'prescriber':
        return (
          <FreeSamplePrescriberStep
            laboratoryName={selectedBrand?.brand ?? ''}
            productName={selectedProductName}
            form={prescriberForm}
            onFormChange={setPrescriberForm}
            isResolvingSpecialty={isResolvingSpecialty}
            onBack={() => setStep('address')}
            onConfirm={handleConfirmPrescriber}
            onClose={handleClose}
          />
        );
      case 'consent':
        return (
          <FreeSampleConsentStep
            laboratoryName={selectedBrand?.brand ?? ''}
            optoutUrl={program?.optoutUrl}
            termsUrl={program?.partnerProgramUrl}
            shippingConsent={shippingConsent}
            communicationsConsent={communicationsConsent}
            onShippingConsentChange={setShippingConsent}
            onCommunicationsConsentChange={setCommunicationsConsent}
            isLoading={isSubmitting}
            onBack={() => setStep('prescriber')}
            onConfirm={handleConfirmConsent}
            onClose={handleClose}
            onEventEmission={handleEventEmission}
            validationCode={prescription?.CodigoValidacao}
          />
        );
      case 'success': {
        const medicineNames = (selectedBrand?.items ?? [])
          .filter((item) => selectedSkus.includes(item.sku))
          .map((item) => item.medicineName);

        return (
          <FreeSampleSuccessStep
            brand={selectedBrand?.brand ?? ''}
            medicineNames={medicineNames}
            address={deliveryAddress}
            pharmaceuticalPageUrl={program?.pharmaceuticalPageUrl}
            pharmaceuticalPhone={program?.pharmaceuticalPhone}
            onClose={handleClose}
            onRequestCompletedView={emitRequestCompleted}
          />
        );
      }
      case 'interest':
        return selectedBrand && selectedInterestItem ? (
          <FreeSampleInterestStep
            brand={selectedBrand.brand}
            item={selectedInterestItem}
            email={prescriberForm.email}
            phone={prescriberForm.phone}
            onEmailChange={(email) => setPrescriberForm((prev) => ({ ...prev, email }))}
            onPhoneChange={(phone) => setPrescriberForm((prev) => ({ ...prev, phone }))}
            birthdate={prescriberForm.birthdate}
            onBirthdateChange={(birthdate) => setPrescriberForm((prev) => ({ ...prev, birthdate }))}
            specialty={prescriberForm.specialty}
            onSpecialtyChange={(specialty) => setPrescriberForm((prev) => ({ ...prev, specialty }))}
            isResolvingSpecialty={isResolvingSpecialty}
            shippingConsent={shippingConsent}
            communicationsConsent={communicationsConsent}
            onShippingConsentChange={setShippingConsent}
            onCommunicationsConsentChange={setCommunicationsConsent}
            isLoading={isSubmitting}
            onBack={() => setStep('address')}
            onConfirm={handleConfirmInterest}
            onClose={handleClose}
          />
        ) : null;
      case 'interestSuccess':
        return (
          <FreeSampleInterestSuccessStep
            brand={selectedBrand?.brand ?? ''}
            medicineName={selectedInterestItem?.medicineName ?? ''}
            onClose={handleClose}
            onRequestCompletedView={emitRequestCompleted}
          />
        );
      case 'empty':
        return <FreeSampleEmptyStep onClose={handleClose} />;
      case 'error':
        return <FreeSampleErrorStep content={errorContent} onClose={handleClose} />;
      case 'catalog':
      default:
        return (
          <FreeSampleLaboratoriesStep
            search={catalogSearch}
            brands={filteredBrands}
            isLoading={isLoadingCatalog}
            onSearchChange={setCatalogSearch}
            onSelectBrand={handleSelectBrand}
            onClose={handleClose}
            onEventEmission={handleEventEmission}
          />
        );
    }
  };

  return isMobile ? (
    <Modal isOpen={isOpen} onClose={handleClose} size="full" isCentered={false}>
      <ModalOverlay />
      <ModalContent
        maxW="100vw"
        maxH={APP_VIEWPORT_HEIGHT}
        m={0}
        borderRadius={0}
        height={APP_VIEWPORT_HEIGHT}
        display="flex"
        flexDirection="column"
      >
        <Box height={APP_VIEWPORT_HEIGHT} display="flex" flexDirection="column" overflow="hidden">
          {renderContent()}
        </Box>
      </ModalContent>
    </Modal>
  ) : (
    <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="md">
      <DrawerOverlay />
      <DrawerContent>{renderContent()}</DrawerContent>
    </Drawer>
  );
};

export default FreeSampleSidebar;
